import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

export function Family() {
  const { family } = useAuth();
  const { t } = useLanguage();

  const AGE_GROUPS = [
    { value: 'child_5_8', label: t('child58'), icon: '👶' },
    { value: 'child_9_12', label: t('child912'), icon: '🧒' },
    { value: 'teen', label: t('teen'), icon: '🧑' },
    { value: 'adult', label: t('adult'), icon: '👨' },
  ];
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberAge, setNewMemberAge] = useState('child_5_8');

  const { data: membersData, isLoading } = useQuery({
    queryKey: ['familyMembers'],
    queryFn: () => api.getFamilyMembers()
  });

  const { data: leaderboardData } = useQuery({
    queryKey: ['leaderboard'],
    queryFn: () => api.getLeaderboard()
  });

  const addMemberMutation = useMutation({
    mutationFn: () => api.addFamilyMember(newMemberName, newMemberAge),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['familyMembers'] });
      setShowAddForm(false);
      setNewMemberName('');
      setNewMemberAge('child_5_8');
    }
  });

  const members = membersData?.members || [];
  const leaderboard = leaderboardData?.leaderboard || [];

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl mb-1">{family?.name || t('myFamily')}</h1>
          <p className="text-gray-600">{t('manageFamily')}</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowAddForm(true)}
        >
          + {t('addMember')}
        </button>
      </div>

      {/* Add Member Form */}
      {showAddForm && (
        <div className="card mb-8 fade-in">
          <h3 className="font-semibold mb-4">{t('addFamilyMember')}</h3>
          <div className="grid grid-2 gap-4">
            <div>
              <label className="label">{t('name')}</label>
              <input
                type="text"
                className="input"
                value={newMemberName}
                onChange={(e) => setNewMemberName(e.target.value)}
                placeholder={t('childName')}
              />
            </div>
            <div>
              <label className="label">{t('ageGroup')}</label>
              <select
                className="input"
                value={newMemberAge}
                onChange={(e) => setNewMemberAge(e.target.value)}
              >
                {AGE_GROUPS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.icon} {g.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-4 mt-4">
            <button
              className="btn btn-primary"
              onClick={() => addMemberMutation.mutate()}
              disabled={!newMemberName || addMemberMutation.isPending}
            >
              {addMemberMutation.isPending ? t('adding') : t('addMember')}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowAddForm(false)}
            >
              {t('cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-2 gap-8">
        {/* Family Members */}
        <div>
          <h2 className="text-xl mb-4">{t('familyMembers')}</h2>
          {isLoading ? (
            <div className="card">{t('loading')}</div>
          ) : members.length === 0 ? (
            <div className="card empty-state">
              <p className="empty-state-icon">👨‍👩‍👧‍👦</p>
              <p>{t('familyMembers')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {members.map((member: any) => {
                // Use streak from leaderboard if available (more accurate)
                const leaderboardEntry = leaderboard.find((e: any) => e.id === member.id);
                const memberWithStreak = leaderboardEntry
                  ? { ...member, current_streak: leaderboardEntry.current_streak }
                  : member;
                return <MemberCard key={member.id} member={memberWithStreak} t={t} AGE_GROUPS={AGE_GROUPS} />;
              })}
            </div>
          )}
        </div>

        {/* Leaderboard */}
        <div>
          <h2 className="text-xl mb-4">{t('familyLeaderboard')} 🏆</h2>
          <div className="card">
            {leaderboard.length === 0 ? (
              <div className="empty-state">
                <p>{t('completeToAppear')}</p>
              </div>
            ) : (
              <div>
                {leaderboard.map((entry: any, index: number) => (
                  <div key={entry.id} className="leaderboard-item">
                    <span className={`leaderboard-rank ${getRankClass(index)}`}>
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                    </span>
                    <div
                      className="avatar"
                      style={{ backgroundColor: entry.avatar_color || '#6366f1' }}
                    >
                      {entry.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">{entry.name}</p>
                      <p className="text-sm text-gray-600">
                        {getAgeGroupLabel(entry.age_group, AGE_GROUPS)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg" style={{ color: 'var(--primary)' }}>
                        {entry.current_streak}
                      </p>
                      <p className="text-xs text-gray-500">{t('dayStreak')}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MemberCard({ member, t, AGE_GROUPS }: { member: any; t: (key: any) => string; AGE_GROUPS: Array<{ value: string; label: string; icon: string }> }) {
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicLink, setMagicLink] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleGenerateLink = async () => {
    setIsGenerating(true);
    try {
      const result = await api.generateMagicLink(member.id);
      setMagicLink(result.magicLink);
      setShowMagicLink(true);
    } catch (err) {
      console.error('Failed to generate link:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyLink = () => {
    if (magicLink) {
      navigator.clipboard.writeText(magicLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="card">
      <div className="flex items-center gap-4">
        <div
          className="avatar"
          style={{ backgroundColor: member.avatar_color || '#6366f1' }}
        >
          {member.name?.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-semibold">{member.name}</p>
            {member.is_parent && (
              <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                {t('parent')}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">
            {getAgeGroupLabel(member.age_group, AGE_GROUPS)}
          </p>
        </div>
        <div className="text-right">
          <div className="streak-badge">
            🔥 {member.current_streak || 0}
          </div>
        </div>
      </div>

      {/* Magic Link Section */}
      <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
        {!showMagicLink ? (
          <button
            onClick={handleGenerateLink}
            disabled={isGenerating}
            className="btn btn-secondary"
            style={{ width: '100%', fontSize: '0.875rem' }}
          >
            {isGenerating ? `⏳ ${t('loading')}` : `🔗 ${t('generateLoginLink')}`}
          </button>
        ) : (
          <div>
            <p className="text-sm text-gray-600 mb-2">
              {t('shareLink')} {member.name}:
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                readOnly
                value={magicLink || ''}
                className="input"
                style={{ fontSize: '0.75rem', flex: 1 }}
              />
              <button
                onClick={handleCopyLink}
                className="btn btn-primary"
                style={{ padding: '0.5rem 1rem', fontSize: '0.75rem' }}
              >
                {copied ? `✓ ${t('copied')}` : t('copy')}
              </button>
            </div>
            <button
              onClick={handleGenerateLink}
              disabled={isGenerating}
              className="btn btn-secondary"
              style={{ width: '100%', marginTop: '0.5rem', fontSize: '0.75rem' }}
            >
              🔄 {t('regenerateLink')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function getAgeGroupLabel(ageGroup: string, AGE_GROUPS: Array<{ value: string; label: string; icon: string }>): string {
  const group = AGE_GROUPS.find(g => g.value === ageGroup);
  return group ? group.label : ageGroup;
}

function getRankClass(index: number): string {
  if (index === 0) return 'gold';
  if (index === 1) return 'silver';
  if (index === 2) return 'bronze';
  return '';
}
